import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase'; // Adjust path as needed
import { useAuth } from '../contexts/AuthContext'; // Adjust path as needed
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Grid, // Using Grid v2
} from '@mui/material';
import { filterNonExpiryProducts } from '../utils/formatters';

// Add interfaces for fetched data
interface Cluster {
  id: string;
  name: string;
}

interface Product {
  id: string;
  name: string;
  cost: number;
}

// Match the ENUM values defined in the database
type AdminRequestType = 'bulk_sim_upload' | 'wallet_topup' | 'cluster_rule_change' | 'other';

export function SubmitRequest() {
  const { user, profile } = useAuth();
  const [requestType, setRequestType] = useState<AdminRequestType | ''>( '' );
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // State for fetched data and its loading status
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [dataLoading, setDataLoading] = useState(true); 
  const [dataError, setDataError] = useState<string | null>(null);

  // --- Placeholder states for specific request types --- 
  // These would be filled based on conditional rendering
  const [quantity, setQuantity] = useState<number | ''>( '' );
  const [clusterId, setClusterId] = useState<string | ''>( '' );
  const [productId, setProductId] = useState<string | ''>( '' );
  const [amount, setAmount] = useState<number | ''>( '' );
  // --- End Placeholder states --- 

  console.log("SubmitRequest component rendering...");

  // Fetch Clusters and Products on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!profile?.organization_id) {
        setDataError("Organization context not available.");
        setDataLoading(false);
        return;
      }
      setDataLoading(true);
      setDataError(null);
      try {
        const [clusterRes, productRes] = await Promise.all([
          supabase
            .from('clusters')
            .select('id, name')
            .eq('organization_id', profile.organization_id)
            .order('name'),
          supabase
            .from('products')
            .select('id, name, cost')
            .eq('is_active', true)
            .order('cost'),
        ]);

        if (clusterRes.error) throw clusterRes.error;
        setClusters(clusterRes.data || []);

        if (productRes.error) throw productRes.error;
        setProducts(productRes.data || []);

      } catch (err: any) {
        console.error("Error fetching clusters/products:", err);
        setDataError(err.message || 'Failed to load data for dropdowns.');
        setClusters([]);
        setProducts([]);
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, [profile?.organization_id]); // Re-run if organization_id changes

  const handleTypeChange = (event: SelectChangeEvent<AdminRequestType | '' >) => {
    setRequestType(event.target.value as AdminRequestType | '' );
    // Reset specific fields when type changes
    setQuantity('');
    setClusterId('');
    setProductId('');
    setAmount('');
    setDetails(''); // Clear generic details too if needed
  };

  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    if (!profile || !requestType) {
      setError('Missing required information (profile or request type).');
      setLoading(false);
      return;
    }

    // Construct request_details based on type (Example)
    let requestDetailsJson = {};
    if (requestType === 'bulk_sim_upload') {
      requestDetailsJson = { quantity, clusterId, productId, uploadNotes: details };
    } else if (requestType === 'wallet_topup') {
      requestDetailsJson = { amount, reason: details };
    } else {
      requestDetailsJson = { genericDetails: details };
    }

    const requestData = {
      organization_id: profile.organization_id,
      requesting_user_id: user?.id,
      request_type: requestType,
      status: 'pending', // Default status
      request_details: requestDetailsJson,
      // approving_user_id and superadmin_notes will be NULL initially
    };

    // console.log("Submitting request:", requestData); // Keep or remove debug log

    try {
      // Implement actual Supabase insert
      const { error: insertError } = await supabase.from('admin_requests').insert(requestData);
      if (insertError) throw insertError;

      // Remove simulation code
      // await new Promise(resolve => setTimeout(resolve, 1000)); 
      
      setSuccessMessage('Request submitted successfully!');
      // Reset form
      setRequestType('');
      setDetails('');
      setQuantity('');
      setClusterId('');
      setProductId('');
      setAmount('');

    } catch (err: any) {
      console.error("Error submitting request:", err);
      setError(err.message || 'Failed to submit request.');
    } finally {
      setLoading(false);
    }
  }, [profile, user, requestType, details, quantity, clusterId, productId, amount]);

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Submit Administrative Request
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel id="request-type-label">Request Type</InputLabel>
                <Select
                  labelId="request-type-label"
                  value={requestType}
                  label="Request Type"
                  onChange={handleTypeChange}
                >
                  <MenuItem value="bulk_sim_upload">Bulk SIM Upload</MenuItem>
                  <MenuItem value="wallet_topup">Wallet Top-up</MenuItem>
                  <MenuItem value="cluster_rule_change">Cluster Rule Change</MenuItem>
                  <MenuItem value="other">other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* --- Conditional Fields Placeholder --- */}
            {requestType === 'bulk_sim_upload' && (
              <>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField 
                    fullWidth 
                    label="Quantity" 
                    type="number" 
                    value={quantity} 
                    onChange={(e) => setQuantity(e.target.value === '' ? '' : Number(e.target.value))}
                    required
                  />
                </Grid>
                {/* --- Cluster Select Dropdown --- */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth required error={!!dataError}> 
                    <InputLabel id="cluster-select-label">Cluster</InputLabel>
                    <Select
                      labelId="cluster-select-label"
                      value={clusterId}
                      label="Cluster"
                      onChange={(e) => setClusterId(e.target.value as string)}
                      disabled={dataLoading}
                    >
                      {dataLoading && <MenuItem disabled><em>Loading clusters...</em></MenuItem>}
                      {dataError && <MenuItem disabled><em>Error loading clusters</em></MenuItem>}
                      {!dataLoading && !dataError && clusters.length === 0 && (
                          <MenuItem disabled><em>No clusters found for your organization.</em></MenuItem>
                      )}
                      {!dataLoading && !dataError && clusters.map((cluster) => (
                        <MenuItem key={cluster.id} value={cluster.id}>
                          {cluster.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* --- Product Select Dropdown --- */}
                 <Grid size={{ xs: 12, sm: 4 }}>
                   <FormControl fullWidth required error={!!dataError}>
                     <InputLabel id="product-select-label">Product</InputLabel>
                     <Select
                       labelId="product-select-label"
                       value={productId}
                       label="Product"
                       onChange={(e) => setProductId(e.target.value as string)}
                       disabled={dataLoading}
                     >
                       {dataLoading && <MenuItem disabled><em>Loading products...</em></MenuItem>}
                       {dataError && <MenuItem disabled><em>Error loading products</em></MenuItem>}
                       {!dataLoading && !dataError && filterNonExpiryProducts(products).map((product) => (
                         <MenuItem key={product.id} value={product.id}>
                           {product.name} ({product.cost}) {/* Display cost for clarity */}
                         </MenuItem>
                       ))}
                     </Select>
                   </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Upload Notes / Details"
                    multiline
                    rows={3}
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                  />
                </Grid>
              </>
            )}

            {requestType === 'wallet_topup' && (
               <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField 
                    fullWidth 
                    label="Amount Requested" 
                    type="number" 
                    value={amount} 
                    onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
                    required
                    inputProps={{ step: "0.01" }} // Allow decimals
                  />
                 </Grid>
                 <Grid size={{ xs: 12 }} >
                    <TextField
                      fullWidth
                      label="Reason / Justification"
                      multiline
                      rows={3}
                      value={details}
                      onChange={(e) => setDetails(e.target.value)}
                      required
                    />
                  </Grid>
              </>
            )}

             {(requestType === 'cluster_rule_change' || requestType === 'other') && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Request Details"
                    multiline
                    rows={4}
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    required
                  />
                </Grid>
             )}
            {/* --- End Conditional Fields --- */}

            <Grid size={{ xs: 12 }}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={loading || !requestType}
              >
                {loading ? <CircularProgress size={24} /> : 'Submit Request'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Container>
  );
} 